<template>
  <div>
    <div :class="containerClass" class="d-flex justify-content-between mt-3">
      <h2>
        Assignment Submissions
        <i class="material-icons icon-16pt clickable-item" v-b-popover.hover.top="'View and grade student assignment submissions.'">
          info
        </i>
      </h2>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <!-- Tabs -->
          <b-tabs content-class="mt-3">
            <b-tab title="GRADING REQUIRED" :active="activeTab === 'graded'" @click="handleTabClick('graded')">
            </b-tab>
            <b-tab title="GRADING NOT REQUIRED" :active="activeTab === 'ungraded'" @click="handleTabClick('ungraded')">
            </b-tab>
          </b-tabs>

          <div class="card-header" style="padding: 1.5rem 1.25rem;">
            <div class="d-flex align-items-center flex-wrap" style="white-space: nowrap; gap: 1rem; margin-bottom: 1.5rem;">
              <div class="flex-grow-0">
                <form class="search-form search-form--light d-lg-inline-flex mb-0" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search by student name"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>

              <div class="flex-grow-0">
                <v-select
                  class="form-control v-select-custom"
                  style="min-width: 200px;"
                  label="title"
                  v-model="selectedProgram"
                  :reduce="pg => pg.id"
                  placeholder="All Program Categories"
                  :options="allPrograms"
                  :loading="areProgramsLoading"
                  @input="onProgramFilter"
                  :clearable="true"
                >
                </v-select>
              </div>

              <div class="flex-grow-0">
                <v-select
                  class="form-control v-select-custom"
                  style="min-width: 200px;"
                  label="title"
                  v-model="selectedTag"
                  :reduce="t => t.id"
                  placeholder="Filter by Tag"
                  :options="tagsList"
                  :loading="areTagsLoading"
                  @input="onTagFilter"
                  :clearable="true"
                >
                </v-select>
              </div>

              <div class="flex-grow-0" v-if="isGradedTab">
                <v-select
                  class="form-control v-select-custom"
                  style="min-width: 200px;"
                  v-model="selectedStatus"
                  label="label"
                  :reduce="status => status.value"
                  placeholder="Filter by Status"
                  :options="statusOptions"
                  @input="onStatusFilter"
                  :clearable="true"
                >
                </v-select>
              </div>
            </div>

            <b-table
              :fields="tableColumns"
              :items="submissions"
              v-if="submissions.length"
              :busy="isLoading"
              head-variant="light"
              class="table-nowrap"
              hover
              responsive
              @row-clicked="rowClicked"
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Loading...</strong>
                </div>
              </template>

              <template #cell(student_name)="data">
                <strong>{{ data.item.student_name }}</strong>
              </template>

              <template #cell(assignment_title)="data">
                {{ data.item.assignment_title }}
              </template>

              <template #cell(course_name)="data">
                {{ data.item.course_name || '-' }}
              </template>

              <template #cell(chapter_title)="data">
                {{ data.item.chapter_title || '-' }}
              </template>

              <template #cell(lesson_title)="data">
                {{ data.item.lesson_title || '-' }}
              </template>

              <template #cell(attempt_number)="data">
                {{ data.item.attempt_number || '-' }}
              </template>

              <template #cell(submitted_at)="data">
                {{ formatDate(data.item.submitted_at) }}
              </template>

              <template #cell(review_status)="data">
                <div class="d-flex align-items-center justify-content-center">
                  <b-badge :variant="getStatusVariant(data.item.review_status)">
                    <i
                      v-if="getStatusIcon(data.item.review_status)"
                      class="material-icons icon-12pt mr-1"
                    >{{ getStatusIcon(data.item.review_status) }}</i>
                    {{ formatStatus(data.item.review_status) }}
                  </b-badge>
                  <i
                    v-if="needsHumanReview(data.item)"
                    class="material-icons icon-14pt text-warning ml-1 review-alert-icon"
                    v-b-tooltip.hover.top="'Needs human review'"
                  >
                    assignment_late
                  </i>
                </div>
              </template>

              <template #cell(grading_method)="data">
                <b-badge
                  v-if="resolveGradingMethod(data.item)"
                  :variant="getMethodDisplayVariant(resolveGradingMethod(data.item))"
                  pill
                >{{ getMethodLabel(resolveGradingMethod(data.item)) }}</b-badge>
                <span v-else class="text-muted">—</span>
              </template>

              <template #cell(grade_info)="data">
                <span v-if="data.item.grade_info && data.item.review_status !== 'pending'">
                  {{ data.item.grade_info.percentage }}%
                </span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(actions)="data">
                <div class="d-flex justify-content-start align-items-center action-buttons">
                  <b-btn
                    v-if="isGradedTab"
                    size="sm"
                    variant="dark"
                    @click.stop="gradeSubmission(data.item.id)"
                    :disabled="data.item.review_status === 'graded'"
                    class="grade-btn action-btn"
                    :style="gradeButtonVars"
                  >
                    <i class="material-icons icon-16pt mr-1">star</i>
                    {{ data.item.review_status === 'graded' ? 'View' : 'Grade' }}
                  </b-btn>
                  <b-btn
                    v-if="isGradedTab && data.item.review_status === 'ai_grading_failed'"
                    size="sm"
                    variant="outline-danger"
                    @click.stop="retryAi(data.item)"
                    :disabled="!!data.item.__retrying"
                    class="action-btn ml-1"
                  >
                    <b-spinner v-if="data.item.__retrying" small></b-spinner>
                    <i v-else class="material-icons icon-16pt mr-1">refresh</i>
                    Retry AI
                  </b-btn>
                  <b-btn
                    v-else
                    size="sm"
                    variant="link"
                    @click.stop="viewSimpleSubmission(data.item)"
                    class="view-eye-btn action-btn"
                    :style="viewEyeButtonVars"
                    :title="'View Submission'"
                  >
                    <i class="material-icons">visibility</i>
                  </b-btn>
                </div>
              </template>
            </b-table>

            <div v-if="!isLoading && !submissions.length" class="text-center p-4">
              <p>No submissions found.</p>
            </div>

            <div class="card-footer">
              <pagination
                v-model="currentPage"
                :total-rows="totalSubmissions"
                :per-page="perPage"
                @change="onPageChange"
                aria-controls="submissions-table"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
import Pagination from '../../../components/Ui/Pagination.vue';
import { formatDateTime } from '../../../common/utils';
import { REVIEW_STATUS_META, GRADING_METHOD_META } from '@/common/aiGradingTemplates';

export default {
  components: {
    Pagination,
  },
  extends: Page,

  data() {
    return {
      title: 'Assignment Submissions',
      isLoading: true,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalSubmissions: 0,
      searchTerm: '',
      submissions: [],
      selectedStatus: null,
      selectedProgram: null,
      allPrograms: [],
      areProgramsLoading: false,
      selectedTag: null,
      tagsList: [],
      areTagsLoading: false,
      activeTab: 'graded', // 'graded' or 'ungraded'
      statusOptions: [
        { label: 'Pending', value: 'pending' },
        { label: 'In Review', value: 'in_review' },
        { label: 'Graded', value: 'graded' },
        { label: 'AI Graded', value: 'ai_graded' },
        { label: 'AI Grading Failed', value: 'ai_grading_failed' },
      ],
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    isGradedTab() {
      return this.activeTab === 'graded';
    },
    primaryColor() {
      const entity = (this.getLoggedInUser && this.getLoggedInUser.linked_entity) || {};
      return entity.primary_color || '#3b82f6';
    },
    secondaryColor() {
      const entity = (this.getLoggedInUser && this.getLoggedInUser.linked_entity) || {};
      return entity.secondary_color || '#1f2937';
    },
    gradeButtonVars() {
      return {
        '--grade-btn-color': this.secondaryColor || this.primaryColor || '#1f2937',
      };
    },
    viewAttemptsLaunchVars() {
      return {
        '--launch-btn-color': this.primaryColor || this.secondaryColor || '#3b82f6',
      };
    },
    viewEyeButtonVars() {
      return {
        '--eye-btn-color': this.primaryColor || this.secondaryColor || '#3b82f6',
      };
    },
    tableColumns() {
      const lessonColumns = [
        { key: 'student_name', label: 'Student', sortable: false },
        { key: 'assignment_title', label: 'Assignment', sortable: false },
        { key: 'course_name', label: 'Course', sortable: false },
        { key: 'chapter_title', label: 'Chapter', sortable: false },
        { key: 'lesson_title', label: 'Lesson', sortable: false },
      ];

      const trailingColumns = [
        { key: 'attempt_number', label: 'Attempt', sortable: false, class: 'text-center' },
        { key: 'created_at', label: 'Submitted', formatter: value => (value ? formatDateTime(value) : value),},
      ];

      if (this.isGradedTab) {
        // Graded submissions - show status, method, and grade
        return [
          { key: 'actions', label: '', sortable: false, class: 'text-left actions-column' },
          ...lessonColumns,
          { key: 'review_status', label: 'Status', sortable: false, class: 'text-center' },
          { key: 'grading_method', label: 'Method', sortable: false, class: 'text-center' },
          { key: 'grade_info', label: 'Grade', sortable: false, class: 'text-center' },
          ...trailingColumns,
        ];
      } else {
        // Simple submissions - no status or grade
        return [
          ...lessonColumns,
          ...trailingColumns,
          { key: 'actions', label: '', sortable: false, class: 'text-right' },
        ];
      }
    },
  },

  methods: {
    ...mapActions('gradedSubmissions', ['getGradedSubmissions', 'retryAiGrade']),
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('attendance', ['getTags']),
    formatDateTime,
    async fetchTags() {
      this.areTagsLoading = true;
      try {
        const res = await this.getTags({
          ...(this.getLoggedInUser.linked_entity && { school__id: this.getLoggedInUser.linked_entity.id }),
        });
        this.tagsList = get(res, 'data.data', []) || [];
      } catch (e) {
        this.tagsList = [];
      }
      this.areTagsLoading = false;
    },
    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getAllPrograms();
        this.allPrograms = res.data;
      } catch (e) {
        this.allPrograms = [];
      }
      this.areProgramsLoading = false;
    },
    async retryAi(item) {
      this.$set(item, '__retrying', true);
      try {
        await this.retryAiGrade(item.id);
        this.makeToast({ variant: 'success', msg: 'AI grading re-queued.' });
        await this.fetchSubmissions();
      } catch (e) {
        const msg = (e.response && (e.response.data.detail || e.response.data.message
          || e.response.data.error)) || 'Failed to retry AI grading';
        this.makeToast({ variant: 'danger', msg });
      } finally {
        this.$set(item, '__retrying', false);
      }
    },
    getStatusIcon(status) {
      const meta = REVIEW_STATUS_META[status];
      return meta ? meta.icon : '';
    },
    getMethodLabel(method) {
      const meta = GRADING_METHOD_META[method];
      return meta ? meta.label : method;
    },
    getMethodVariant(method) {
      const meta = GRADING_METHOD_META[method];
      return meta ? meta.variant : 'secondary';
    },
    getMethodDisplayVariant(method) {
      if (method === 'ai_human_reviewed') {
        return 'dark';
      }
      const variant = this.getMethodVariant(method);
      if (typeof variant === 'string' && variant.startsWith('outline-')) {
        return variant.replace('outline-', '') || 'secondary';
      }
      return variant;
    },
    resolveGradingMethod(item) {
      return (item && item.grade_info && item.grade_info.grading_method) || item.grading_method || '';
    },
    needsHumanReview(item) {
      return item && item.review_status === 'in_review' && this.resolveGradingMethod(item) === 'ai';
    },
    onPageChange(pageNum) {
      this.currentPage = pageNum;
      this.fetchSubmissions();
    },

    async fetchSubmissions() {
      this.isLoading = true;
      try {
        const params = {
          limit: this.perPage,
          offset: (this.currentPage - 1) * this.perPage,
          graded: this.isGradedTab,
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.selectedStatus && { status: this.selectedStatus }),
          ...(this.selectedProgram && { program_id: this.selectedProgram }),
          ...(this.selectedTag && { tag_id: this.selectedTag }),
        };

        const response = await this.getGradedSubmissions(params);
        this.submissions = response.data.results ? response.data.results : response.data;
        this.totalSubmissions = response.data.count || response.data.length;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to fetch submissions' });
      } finally {
        this.isLoading = false;
      }
    },

    handleTabClick(tabKey) {
      if (this.activeTab === tabKey) return;
      
      this.activeTab = tabKey;
      this.currentPage = 1;
      this.selectedStatus = null;
      this.searchTerm = '';
      
      // Update query parameter
      if (this.$route.query.tab !== tabKey) {
        this.$router.replace({ 
          query: { ...this.$route.query, tab: tabKey } 
        }).catch(() => {});
      }
      
      this.fetchSubmissions();
    },

    rowClicked(row) {
      if (this.isGradedTab) {
        this.gradeSubmission(row.id);
      } else {
        this.viewSimpleSubmission(row);
      }
    },


    gradeSubmission(id) {
      this.$router.push({
        name: 'grade-submission',
        params: { id }
      });
    },

    viewSimpleSubmission(submission) {
      this.$router.push({
        name: 'simple-submission-detail',
        params: { id: submission.id },
        query: {
          student_id: submission.student || submission.student_id
        }
      });
    },

    onSearch() {
      this.currentPage = 1;
      this.debouncedSubmissionSearch();
    },

    onStatusFilter() {
      this.currentPage = 1;
      this.fetchSubmissions();
    },

    onProgramFilter() {
      this.currentPage = 1;
      this.fetchSubmissions();
    },

    onTagFilter() {
      this.currentPage = 1;
      this.fetchSubmissions();
    },

    debouncedSubmissionSearch: debounce(function() {
      this.fetchSubmissions();
    }, 500),

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    formatStatus(status) {
      const meta = REVIEW_STATUS_META[status];
      return meta ? meta.label : status;
    },

    getStatusVariant(status) {
      const meta = REVIEW_STATUS_META[status];
      return meta ? meta.variant : 'secondary';
    },
  },

  async created() {
    // Initialize activeTab from query parameter if present
    const tabFromQuery = this.$route.query.tab;
    const validTabs = ['graded', 'ungraded'];
    if (tabFromQuery && validTabs.includes(tabFromQuery)) {
      this.activeTab = tabFromQuery;
    }

    this.fetchPrograms();
    this.fetchTags();
    this.fetchSubmissions();
  },
};
</script>

<style scoped>
.clickable-item {
  cursor: pointer;
}

.review-alert-icon {
  cursor: help;
}

/* Tab styling */
::v-deep .nav-tabs {
  border-bottom: 3px solid #e5e7eb;
  padding: 0.25rem 1.5rem 0;
  background: #f9fafb;
  min-height: 64px;
  display: flex;
  align-items: flex-end;
}

::v-deep .nav-tabs .nav-link {
  border: none;
  color: #6b7280;
  font-weight: 600;
  font-size: 1.05rem;
  padding: 1.25rem 2.75rem;
  margin-bottom: -4px;
  border-bottom: 4px solid transparent;
  transition: all 0.2s ease;
  background: transparent;
  border-radius: 12px 12px 0 0;
}

::v-deep .nav-tabs .nav-link:hover {
  color: #111827;
  border-bottom-color: #d1d5db;
  background: #ffffff;
}

::v-deep .nav-tabs .nav-link.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
  background: #ffffff;
  font-weight: 700;
}

/* Button styling */
.action-buttons {
  gap: 0.5rem;
}

::v-deep .actions-column {
  width: 118px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  border-radius: 8px;
  padding: 0.35rem 0.85rem;
  font-weight: 600;
  font-size: 0.72rem;
  letter-spacing: 0.2px;
  text-transform: none;
  line-height: 1;
  transition: all 0.2s ease;
}

.grade-btn {
  background: var(--grade-btn-color, #1f2937);
  border-color: var(--grade-btn-color, #1f2937);
  color: #ffffff;
}

.grade-btn:hover:not(:disabled) {
  filter: brightness(0.9);
}

.grade-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.grade-btn .material-icons {
  font-size: 16px;
  vertical-align: middle;
}

.view-attempts-launch {
  padding: 0.35rem;
  border-radius: 50%;
  width: 34px;
  height: 34px;
  border: 1.5px solid var(--launch-btn-color, #3b82f6);
  color: var(--launch-btn-color, #3b82f6);
  background: #ffffff;
}

.view-attempts-launch .material-icons {
  font-size: 20px;
  text-transform: none;
}

.view-attempts-launch:hover {
  background: var(--launch-btn-color, #3b82f6);
  color: #ffffff;
}

.view-eye-btn {
  padding: 0.35rem;
  border-radius: 50%;
  width: 34px;
  height: 34px;
  border: 1.5px solid var(--eye-btn-color, #3b82f6);
  color: var(--eye-btn-color, #3b82f6);
  background: #ffffff;
}

.view-eye-btn .material-icons {
  font-size: 20px;
  text-transform: none;
}

.view-eye-btn:hover {
  background: var(--eye-btn-color, #3b82f6);
  color: #ffffff;
}
</style>
